/**
 * FleetMap - Phase 1 (Static, Mission-Driven)
 * 
 * Full-screen Leaflet map showing fleet activity.
 * Pins are drone-shaped SVG icons colored by activity_status, pulse on in_flight.
 * 
 * Features:
 * - Filter chips: All / In-flight / Today / Maintenance / Available
 * - Time slider: -2 days <-> now <-> +24h forward
 * - Click pin -> side panel with aircraft details
 * - Auto-refresh every 60s
 * - Tier A Free: cap 3 visible pins + upgrade prompt
 * 
 * No telemetry/WebSocket in Phase 1.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Plane, Clock, Wrench, Calendar, CheckCircle, XCircle,
  Battery, Radio, Shield, ChevronRight, RefreshCw, Filter,
  AlertTriangle, Zap, User, MapPin, X, Crown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { toast } from "sonner";

// =============================================================================
// DRONE SVG ICON FACTORY
// =============================================================================

const createDroneIcon = (status, isAnimated = false) => {
  const colors = {
    in_flight: "#22c55e",      // Green
    scheduled_today: "#3b82f6", // Blue
    scheduled_upcoming: "#8b5cf6", // Purple
    maintenance: "#f59e0b",    // Amber
    available: "#6b7280",      // Gray
    retired: "#dc2626",        // Red
  };
  
  const color = colors[status] || colors.available;
  const pulseAnimation = isAnimated ? `
    <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite"/>
  ` : "";
  
  // Drone-shaped SVG
  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
      <g transform="translate(20,20)">
        ${pulseAnimation ? `<circle cx="0" cy="0" r="18" fill="${color}" opacity="0.3">${pulseAnimation}</circle>` : ""}
        <!-- Drone body -->
        <ellipse cx="0" cy="0" rx="8" ry="5" fill="${color}" stroke="#fff" stroke-width="1.5"/>
        <!-- Arms -->
        <line x1="-12" y1="-8" x2="12" y2="8" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
        <line x1="12" y1="-8" x2="-12" y2="8" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
        <!-- Rotors -->
        <circle cx="-12" cy="-8" r="4" fill="${color}" stroke="#fff" stroke-width="1"/>
        <circle cx="12" cy="-8" r="4" fill="${color}" stroke="#fff" stroke-width="1"/>
        <circle cx="-12" cy="8" r="4" fill="${color}" stroke="#fff" stroke-width="1"/>
        <circle cx="12" cy="8" r="4" fill="${color}" stroke="#fff" stroke-width="1"/>
        <!-- Center dot -->
        <circle cx="0" cy="0" r="2" fill="#fff"/>
      </g>
    </svg>
  `;
  
  return L.divIcon({
    html: svgString,
    className: "drone-marker",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

// =============================================================================
// STATUS HELPERS
// =============================================================================

const getStatusIcon = (status) => {
  const icons = {
    in_flight: <Plane className="h-4 w-4" />,
    scheduled_today: <Calendar className="h-4 w-4" />,
    scheduled_upcoming: <Clock className="h-4 w-4" />,
    maintenance: <Wrench className="h-4 w-4" />,
    available: <CheckCircle className="h-4 w-4" />,
    retired: <XCircle className="h-4 w-4" />,
  };
  return icons[status] || icons.available;
};

const getStatusColor = (status) => {
  const colors = {
    in_flight: "bg-green-500",
    scheduled_today: "bg-blue-500",
    scheduled_upcoming: "bg-purple-500",
    maintenance: "bg-amber-500",
    available: "bg-gray-500",
    retired: "bg-red-500",
  };
  return colors[status] || colors.available;
};

// Format datetime
const formatTime = (dateStr, language) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleTimeString(language === "cs" ? "cs-CZ" : "en-US", {
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatDateTime = (dateStr, language) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString(language === "cs" ? "cs-CZ" : "en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// =============================================================================
// MAP CENTER CONTROL
// =============================================================================

function MapCenterControl({ fleet }) {
  const map = useMap();
  
  useEffect(() => {
    if (fleet.length > 0) {
      const positions = fleet
        .filter(a => a.current_position)
        .map(a => [a.current_position.lat, a.current_position.lng]);
      
      if (positions.length > 0) {
        const bounds = L.latLngBounds(positions);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [fleet, map]);
  
  return null;
}

// =============================================================================
// SIDE PANEL
// =============================================================================

function AircraftSidePanel({ aircraft, language, t, onClose, onNavigate }) {
  if (!aircraft) return null;
  
  const statusLabel = language === "cs" 
    ? aircraft.activity_status_label_cs 
    : aircraft.activity_status_label_en;
  
  return (
    <div className="absolute right-0 top-0 h-full w-96 bg-background border-l shadow-xl z-10 overflow-y-auto">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">{aircraft.aircraft_registration}</h3>
            <p className="text-sm text-muted-foreground">{aircraft.aircraft_model}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Status Badge */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white mb-4 ${getStatusColor(aircraft.activity_status)}`}>
          {getStatusIcon(aircraft.activity_status)}
          <span className="font-medium">{statusLabel}</span>
        </div>
        
        {/* Aircraft Details */}
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("fleetMap.aircraftDetails")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("fleetMap.serial")}</span>
              <span className="font-mono">{aircraft.aircraft_serial || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("fleetMap.mtom")}</span>
              <span>{aircraft.aircraft_mtom_kg ? `${aircraft.aircraft_mtom_kg} kg` : "-"}</span>
            </div>
          </CardContent>
        </Card>
        
        {/* Active Mission */}
        {aircraft.active_mission && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Plane className="h-4 w-4" />
                {aircraft.activity_status === "in_flight" 
                  ? t("fleetMap.currentMission") 
                  : t("fleetMap.upcomingMission")
                }
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("fleetMap.missionName")}</span>
                <span>{aircraft.active_mission.mission_name || aircraft.active_mission.mission_id}</span>
              </div>
              {aircraft.active_mission.pilot_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {t("fleetMap.pilot")}
                  </span>
                  <span>{aircraft.active_mission.pilot_name}</span>
                </div>
              )}
              {aircraft.active_mission.etot && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("fleetMap.etot")}</span>
                  <span>{formatTime(aircraft.active_mission.etot, language)}</span>
                </div>
              )}
              {aircraft.active_mission.eldt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("fleetMap.eldt")}</span>
                  <span>{formatTime(aircraft.active_mission.eldt, language)}</span>
                </div>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full mt-2"
                onClick={() => onNavigate(`/missions/${aircraft.active_mission.mission_id}`)}
              >
                {t("fleetMap.viewMission")}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Maintenance Info */}
        {aircraft.activity_status === "maintenance" && aircraft.maintenance_end_at && (
          <Card className="mb-4 border-amber-500">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
                <Wrench className="h-4 w-4" />
                {t("fleetMap.inMaintenance")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("fleetMap.endsAt")}</span>
                <span>{formatDateTime(aircraft.maintenance_end_at, language)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">{t("fleetMap.type")}</span>
                <span>{aircraft.maintenance_type || "-"}</span>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Equipment Status */}
        {aircraft.equipment_status && (
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t("fleetMap.equipment")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Battery className="h-4 w-4" />
                  {t("fleetMap.battery")}
                </span>
                <Badge variant={
                  aircraft.equipment_status.battery_health === "good" ? "default" :
                  aircraft.equipment_status.battery_health === "warning" ? "secondary" : "destructive"
                }>
                  {aircraft.equipment_status.battery_installed 
                    ? `${aircraft.equipment_status.battery_cycles || 0} cycles`
                    : t("fleetMap.notInstalled")
                  }
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Radio className="h-4 w-4" />
                  {t("fleetMap.rid")}
                </span>
                <Badge variant={aircraft.equipment_status.rid_active ? "default" : "secondary"}>
                  {aircraft.equipment_status.rid_active ? t("fleetMap.active") : t("fleetMap.inactive")}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {t("fleetMap.rtLicense")}
                </span>
                <Badge variant={aircraft.equipment_status.rt_license_valid ? "default" : "destructive"}>
                  {aircraft.equipment_status.rt_license_valid ? t("fleetMap.valid") : t("fleetMap.expired")}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Actions */}
        <div className="space-y-2">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => onNavigate(`/aircraft/${aircraft.aircraft_id}`)}
          >
            {t("fleetMap.viewAircraft")}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function FleetMap() {
  const { user } = useAuth();
  const { activeOrganizationId, canUseOrganizationScope, scopeVersion } = useOrganizationScope();
  const { translate: t, language } = useLanguage();
  const [fleet, setFleet] = useState([]);
  const [filteredFleet, setFilteredFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [tierCapped, setTierCapped] = useState(false);
  const [tierCapLimit, setTierCapLimit] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Auto-refresh
  const refreshIntervalRef = useRef(null);
  
  // Fetch fleet data (always current time — time slider removed)
  const fetchFleet = useCallback(async (signal) => {
    try {
      const response = await api.get("/fleet/activity", signal ? { signal } : undefined);
      const data = response.data;
      
      setFleet(data.fleet || []);
      setTierCapped(data.tier_capped || false);
      setTierCapLimit(data.tier_cap_limit);
      setTotalCount(data.total_count || 0);
      
    } catch (error) {
      if (error.code === "ERR_CANCELED") return;
      console.error("Failed to fetch fleet:", error);
      if (error.response?.status !== 403) {
        toast.error(language === "cs" ? "Chyba při načítání flotily" : "Failed to load fleet");
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [language]);
  
  // Initial fetch and auto-refresh every 60s
  useEffect(() => {
    const controller = new AbortController();

    setFleet([]);
    setFilteredFleet([]);
    setSelectedAircraft(null);
    setLoading(Boolean(canUseOrganizationScope));

    if (!canUseOrganizationScope) {
      return () => controller.abort();
    }

    fetchFleet(controller.signal);
    
    refreshIntervalRef.current = setInterval(() => {
      fetchFleet(controller.signal);
    }, 60000);
    
    return () => {
      controller.abort();
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [activeOrganizationId, canUseOrganizationScope, fetchFleet, scopeVersion]);
  
  // Apply filters
  useEffect(() => {
    if (activeFilter === "all") {
      setFilteredFleet(fleet);
    } else {
      setFilteredFleet(fleet.filter(a => a.activity_status === activeFilter));
    }
  }, [fleet, activeFilter]);
  
  // Navigate to other routes
  const handleNavigate = (path) => {
    window.location.href = path;
  };
  
  // Filter chips
  const filterOptions = [
    { key: "all", label: t("fleetMap.filterAll"), icon: <Filter className="h-3 w-3" /> },
    { key: "in_flight", label: t("fleetMap.filterInFlight"), icon: <Plane className="h-3 w-3" /> },
    { key: "scheduled_today", label: t("fleetMap.filterToday"), icon: <Calendar className="h-3 w-3" /> },
    { key: "maintenance", label: t("fleetMap.filterMaintenance"), icon: <Wrench className="h-3 w-3" /> },
    { key: "available", label: t("fleetMap.filterAvailable"), icon: <CheckCircle className="h-3 w-3" /> },
  ];
  
  // Count by status for badges
  const statusCounts = fleet.reduce((acc, a) => {
    acc[a.activity_status] = (acc[a.activity_status] || 0) + 1;
    return acc;
  }, {});
  
  // Tile URL - support Tier C offline
  const deploymentTier = process.env.REACT_APP_DEPLOYMENT_TIER || "A";
  const tileUrl = deploymentTier === "C" 
    ? "/tiles/{z}/{x}/{y}.png"  // Local tiles for Tier C
    : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  // `isolate` keeps Leaflet's panes/controls (z-index 200–1000) and the map overlays below the sticky app
  // header (z-30). The height leaves room for that header (h-14) and the page padding (p-4 / md:p-8) of
  // DashboardLayout, so the page does not scroll.
  return (
    <div className="relative isolate h-[calc(100dvh-5.5rem)] w-full md:h-[calc(100dvh-7.5rem)]" data-testid="fleet-map">
      {/* Map */}
      <MapContainer
        center={[50.0755, 14.4378]} // Prague default
        zoom={7}
        className="h-full w-full z-0"
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={tileUrl}
        />
        
        <MapCenterControl fleet={filteredFleet.filter(a => a.current_position)} />
        
        {/* Aircraft markers */}
        {filteredFleet.map((aircraft) => {
          if (!aircraft.current_position) return null;
          
          const isInFlight = aircraft.activity_status === "in_flight";
          const icon = createDroneIcon(aircraft.activity_status, isInFlight);
          
          return (
            <Marker
              key={aircraft.aircraft_id}
              position={[aircraft.current_position.lat, aircraft.current_position.lng]}
              icon={icon}
              eventHandlers={{
                click: () => setSelectedAircraft(aircraft)
              }}
            >
              <Popup>
                <div className="text-center">
                  <strong>{aircraft.aircraft_registration}</strong>
                  <br />
                  <span className="text-sm">
                    {language === "cs" ? aircraft.activity_status_label_cs : aircraft.activity_status_label_en}
                  </span>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Top Controls */}
      <div className="absolute top-4 left-4 z-10 max-w-[calc(100%-6rem)]">
        {/* Filter Chips */}
        <div className="flex flex-wrap gap-2 bg-background/90 backdrop-blur-sm p-2 rounded-lg shadow-lg">
          {filterOptions.map((filter) => (
            <Button
              key={filter.key}
              variant={activeFilter === filter.key ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilter(filter.key)}
              className="flex items-center gap-1"
              data-testid={`filter-${filter.key}`}
            >
              {filter.icon}
              {filter.label}
              {filter.key !== "all" && statusCounts[filter.key] > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {statusCounts[filter.key]}
                </Badge>
              )}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Refresh button */}
      <div className="absolute top-4 right-4 z-10">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchFleet()}
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          {t("actions.refresh")}
        </Button>
      </div>
      
      {/* Tier A Free Cap Warning */}
      {tierCapped && (
        <div className="absolute bottom-20 left-4 right-4 z-10">
          <Alert className="bg-amber-50 border-amber-200">
            <Crown className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">{t("fleetMap.tierCapTitle")}</AlertTitle>
            <AlertDescription className="text-amber-700">
              {t("fleetMap.tierCapDescription", { shown: tierCapLimit, total: totalCount })}
              <Button variant="link" className="text-amber-800 p-0 ml-2 h-auto">
                {t("fleetMap.upgrade")}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}
      
      {/* Footer Summary */}
      <div className="absolute bottom-4 left-4 z-10">
        <Card className="bg-background/90 backdrop-blur-sm">
          <CardContent className="py-2 px-4">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>{statusCounts.in_flight || 0} {t("fleetMap.inFlight")}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span>{statusCounts.scheduled_today || 0} {t("fleetMap.scheduledToday")}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span>{statusCounts.maintenance || 0} {t("fleetMap.maintenance")}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-gray-500" />
                <span>{statusCounts.available || 0} {t("fleetMap.available")}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Side Panel */}
      <AircraftSidePanel
        aircraft={selectedAircraft}
        language={language}
        t={t}
        onClose={() => setSelectedAircraft(null)}
        onNavigate={handleNavigate}
      />
    </div>
  );
}
